import type Map from 'ol/Map';
import { getPointResolution } from 'ol/proj';
import type { AppState } from '../core/types';
import { coordinateFromMap, formatCoordinate, type GeoCoordinate } from '../domain/coordinates';
import { copyText } from './clipboard';

const COPY_FEEDBACK_MS = 1400;

/** Elevation under the cursor, answered from memory (no network per move). */
export interface StatusElevation {
  /** "44.5 m" or "Sin dato (…)" for a map (EPSG:3857) coordinate. */
  read(coordinate: readonly number[]): string;
  /** Called when the source becomes available, fails, etc. */
  onChange(listener: () => void): void;
  openSource(): void;
}

const ENTITY_PLURAL: Readonly<Record<AppState['entityViewLevel'], [string, string]>> = {
  finca: ['Finca', 'Fincas'], suerte: ['Suerte', 'Suertes'], tablon: ['Tablón', 'Tablones'], fragment: ['Fragmento', 'Fragmentos'],
};
export const entityNoun = (level: AppState['entityViewLevel'], count: number): string => ENTITY_PLURAL[level][count === 1 ? 0 : 1];

/** Ground scale denominator for a 96 dpi screen at the view center. */
export function mapScaleDenominator(resolution: number, metersPerPixel: number): number {
  return Number.isFinite(resolution) && Number.isFinite(metersPerPixel) ? Math.round(metersPerPixel * 96 / 0.0254) : NaN;
}

export const formatScale = (denominator: number): string => {
  if (!Number.isFinite(denominator) || denominator <= 0) return '1:—';
  const rounded = denominator >= 10_000 ? Math.round(denominator / 100) * 100 : denominator >= 1_000 ? Math.round(denominator / 10) * 10 : Math.round(denominator);
  return `1:${rounded.toLocaleString('es-NI')}`;
};

/** Compact status bar: CRS · zoom · scale · coordinates (click to copy) · visible entities · selection. */
export class StatusBar {
  private readonly zoom: HTMLElement;
  private readonly scale: HTMLElement;
  private readonly coordinates: HTMLElement;
  private readonly visible: HTMLElement;
  private readonly selection: HTMLElement;
  private frame = 0;
  private pointer: GeoCoordinate | null = null;
  private feedbackTimer = 0;
  private readonly elevationValue: HTMLElement | null;
  private elevationFrame = 0;
  private pointerCoordinate: readonly number[] | null = null;

  constructor(root: ParentNode, private readonly map: Map, private readonly copy: (text: string) => Promise<boolean> = copyText, private readonly elevation: StatusElevation | null = null) {
    const required = (selector: string): HTMLElement => { const element = root.querySelector<HTMLElement>(selector); if (!element) throw new Error(`No se encontró ${selector}`); return element; };
    this.zoom = required('#status-zoom'); this.scale = required('#status-scale'); this.coordinates = required('#coordinates');
    this.visible = required('#status-visible'); this.selection = required('#selection-status');
    map.getView().on('change:resolution', () => this.scheduleView());
    map.on('moveend', () => this.renderView());
    this.elevationValue = elevation ? root.querySelector<HTMLElement>('#status-elevation-value') : null;
    map.on('pointermove', (event) => {
      this.pointer = coordinateFromMap(event.coordinate);
      if (!this.feedbackTimer) this.coordinates.textContent = formatCoordinate(this.pointer, { degrees: true });
      this.pointerCoordinate = event.coordinate;
      this.scheduleElevation();
    });
    if (elevation) {
      elevation.onChange(() => this.scheduleElevation());
      root.querySelector('#status-elevation-source')?.addEventListener('click', () => elevation.openSource());
    }
    this.coordinates.addEventListener('click', () => void this.copyPointer());
    this.coordinates.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); void this.copyPointer(); }
    });
    this.renderView();
  }

  render(counts: { readonly level: AppState['entityViewLevel']; readonly visible: number; readonly hasMaster: boolean; readonly selected: number; readonly selectedAreaHa: number; readonly selectedNoun: string }): void {
    this.visible.textContent = counts.hasMaster ? `${counts.visible.toLocaleString('es-NI')} ${entityNoun(counts.level, counts.visible)} visibles` : 'Sin Maestro';
    this.selection.textContent = counts.selected
      ? `${counts.selected.toLocaleString('es-NI')} ${counts.selectedNoun} seleccionad${/^(finca|suerte|entidad)/i.test(counts.selectedNoun) ? 'a' : 'o'}${counts.selected === 1 ? '' : 's'}${counts.selectedAreaHa > 0 ? ` · ${counts.selectedAreaHa.toLocaleString('es-NI', { maximumFractionDigits: 2 })} ha` : ''}`
      : 'Sin selección';
    this.selection.classList.toggle('active', counts.selected > 0);
  }

  /** Copies the last coordinate under the cursor as "lat, lon" and confirms in place for a moment. */
  async copyPointer(): Promise<void> {
    if (!this.pointer) return;
    const copied = await this.copy(formatCoordinate(this.pointer));
    window.clearTimeout(this.feedbackTimer);
    this.coordinates.textContent = copied ? 'Coordenada copiada' : 'No se pudo copiar';
    this.coordinates.classList.add('copied');
    this.feedbackTimer = window.setTimeout(() => {
      this.feedbackTimer = 0;
      this.coordinates.classList.remove('copied');
      if (this.pointer) this.coordinates.textContent = formatCoordinate(this.pointer, { degrees: true });
    }, COPY_FEEDBACK_MS);
  }

  /** At most one elevation read per animation frame, always for the latest pointer position (stale moves are dropped). */
  private scheduleElevation(): void {
    if (!this.elevation || !this.elevationValue || this.elevationFrame) return;
    this.elevationFrame = window.requestAnimationFrame(() => {
      this.elevationFrame = 0;
      if (!this.pointerCoordinate || !this.elevation || !this.elevationValue) return;
      const text = this.elevation.read(this.pointerCoordinate);
      if (this.elevationValue.textContent !== text) this.elevationValue.textContent = text;
    });
  }

  private scheduleView(): void {
    if (this.frame) return;
    this.frame = window.requestAnimationFrame(() => { this.frame = 0; this.renderView(); });
  }

  private renderView(): void {
    const view = this.map.getView(); const resolution = view.getResolution(); const center = view.getCenter();
    const zoom = view.getZoom();
    this.zoom.textContent = `Zoom ${zoom === undefined ? '—' : zoom.toFixed(2)}`;
    if (resolution === undefined || !center) { this.scale.textContent = '1:—'; return; }
    const metersPerPixel = getPointResolution(view.getProjection(), resolution, center, 'm');
    this.scale.textContent = formatScale(mapScaleDenominator(resolution, metersPerPixel));
  }
}
